import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

export interface ChartTooltipRow {
  key?: string;
  /** The series or slice colour, drawn as a dot before the label. */
  color?: string;
  label: ReactNode;
  value: ReactNode;
}

/**
 * The hover card of every chart. Each chart used to draw its own — an English
 * "Daily:" on one, a `fr-FR` date on another, `bg-background` that vanished
 * into the page in dark mode — so the words and the dates now come from the
 * caller (translated, in the user's language) and only the frame lives here.
 */
export function ChartTooltip({
  title,
  rows,
  className,
}: {
  title?: ReactNode;
  rows: ChartTooltipRow[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-overlay whitespace-nowrap",
        className,
      )}
    >
      {title ? <div className="mb-1 font-medium">{title}</div> : null}
      <div className="space-y-0.5">
        {rows.map((row, index) => (
          <div
            key={row.key ?? index}
            className="flex items-center gap-2 tabular-nums"
          >
            {row.color ? (
              <span
                className="inline-block size-2 shrink-0 rounded-full"
                style={{ background: row.color }}
              />
            ) : null}
            <span className="text-muted-foreground">{row.label}</span>
            <span className="ml-auto pl-3 font-medium">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
